import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Loader2, Sparkles, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { key: 'minimal', label: 'Minimal', desc: 'Fondo bianco/beige, zero props' },
  { key: 'lifestyle', label: 'Lifestyle', desc: 'Caldo, luce naturale, props soft' },
  { key: 'marketplace', label: 'Marketplace', desc: 'Neutro, Amazon/Zalando style' },
  { key: 'vintage', label: 'Vintage', desc: 'Grain pellicola, toni anni 60-70' },
  { key: 'mediterraneo', label: 'Mediterraneo', desc: 'Ceramica, lino, luce dorata' },
];

function useStoredImage(path) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) { setUrl(null); return; }
    let alive = true;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => { if (alive) setUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [path]);
  return url;
}

export default function BrandKitManager({ getHeaders }) {
  const [bk, setBk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingScene, setUploadingScene] = useState(false);
  const logoRef = useRef(null);
  const sceneRef = useRef(null);
  const [instructions, setInstructions] = useState('');
  const [preset, setPreset] = useState('minimal');
  const [suggestions, setSuggestions] = useState([]);
  const [recommended, setRecommended] = useState('');
  const [hasPrefilled, setHasPrefilled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/shops/mine/brand-kit`, { withCredentials: true, headers: getHeaders() });
      setBk(data);
      setInstructions(data.custom_instructions || '');
      setPreset(data.style_preset || 'minimal');
    } catch { /* ignore */ } finally { setLoading(false); }
    try {
      const { data } = await axios.get(`${API}/shops/mine/ai-prompt-suggestions`, { withCredentials: true, headers: getHeaders() });
      setSuggestions(data.suggestions || []);
      setRecommended(data.recommended || '');
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Prefill recommended prompt when textarea is empty (only once, after load)
  useEffect(() => {
    if (hasPrefilled) return;
    if (!bk || !recommended) return;
    if (!instructions || !instructions.trim()) {
      setInstructions(recommended);
      setHasPrefilled(true);
    } else {
      setHasPrefilled(true);
    }
  }, [bk, recommended, instructions, hasPrefilled]);

  const logoUrl = useStoredImage(bk?.logo_path);

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/shops/mine/brand-kit/logo`, fd, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo caricato');
      load();
    } catch { toast.error('Errore upload logo'); }
    finally { setUploadingLogo(false); }
  };

  const uploadScene = async (file) => {
    setUploadingScene(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/shops/mine/brand-kit/scene`, fd, { withCredentials: true, headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Ambientazione aggiunta');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload scena'); }
    finally { setUploadingScene(false); }
  };

  const deleteScene = async (path) => {
    try {
      await axios.delete(`${API}/shops/mine/brand-kit/scene`, { withCredentials: true, headers: getHeaders(), params: { path } });
      toast.success('Ambientazione rimossa');
      load();
    } catch { toast.error('Errore'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/shops/mine/brand-kit`, {
        is_configured: true,
        style_preset: preset,
        custom_instructions: instructions,
      }, { withCredentials: true, headers: getHeaders() });
      toast.success('Brand Kit salvato — tutte le nuove foto AI useranno questo stile');
      load();
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-40 skeleton-shimmer rounded-xl" />;
  if (!bk) return null;

  return (
    <div data-testid="brand-kit-manager" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#C25934] to-[#8B5CF6] text-white flex items-center justify-center flex-shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">🎨 Brand Visual Kit</h3>
          <p className="text-xs text-[#877E78] mt-0.5">
            L'impronta visiva del tuo negozio. Tutte le foto AI dei prodotti seguiranno questo stile.
          </p>
          {bk.is_configured && (
            <p className="text-[11px] text-[#4A6B53] font-medium mt-1">✓ Kit attivo</p>
          )}
        </div>
      </div>

      {/* LOGO */}
      <div>
        <label className="text-xs font-bold text-[#5C554F] uppercase">Logo negozio</label>
        <div className="mt-1 flex items-center gap-3">
          <div className="w-20 h-20 rounded-xl bg-[#F0EBE1] border border-[#E8E3D9] overflow-hidden flex items-center justify-center">
            {logoUrl ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" /> : <ImageIcon size={24} className="text-[#877E78]" />}
          </div>
          <div className="flex-1">
            <Button
              data-testid="upload-logo-btn"
              onClick={() => logoRef.current?.click()}
              disabled={uploadingLogo}
              variant="outline" size="sm"
              className="rounded-full text-xs"
            >
              {uploadingLogo ? <><Loader2 size={12} className="animate-spin mr-1" /> Upload...</> : <><Upload size={12} className="mr-1" /> {bk.logo_path ? 'Sostituisci logo' : 'Carica logo'}</>}
            </Button>
            <p className="text-[10px] text-[#877E78] mt-1">PNG trasparente consigliato · min 512×512 px</p>
          </div>
          <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
        </div>
      </div>

      {/* SCENE REFS */}
      <div>
        <label className="text-xs font-bold text-[#5C554F] uppercase">Foto ambientazione (1-3)</label>
        <p className="text-[10px] text-[#877E78] mt-0.5 mb-2">Il mood del tuo negozio: interno, vetrina, bancone. L'AI estrae palette e atmosfera.</p>
        <div className="grid grid-cols-3 gap-2">
          {(bk.scene_ref_paths || []).map(path => (
            <SceneThumb key={path} path={path} onDelete={() => deleteScene(path)} />
          ))}
          {(bk.scene_ref_paths || []).length < 3 && (
            <button
              data-testid="upload-scene-btn"
              onClick={() => sceneRef.current?.click()}
              disabled={uploadingScene}
              className="aspect-square rounded-lg border-2 border-dashed border-[#C25934]/30 hover:border-[#C25934] flex flex-col items-center justify-center gap-1 text-[#C25934] text-[10px] font-medium"
            >
              {uploadingScene ? <Loader2 size={18} className="animate-spin" /> : <><Upload size={18} /> Aggiungi</>}
            </button>
          )}
        </div>
        <input ref={sceneRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadScene(e.target.files[0])} />
      </div>

      {/* PRESET STILE */}
      <div>
        <label className="text-xs font-bold text-[#5C554F] uppercase mb-2 block">Stile visivo</label>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map(p => (
            <button
              key={p.key}
              data-testid={`preset-${p.key}`}
              onClick={() => setPreset(p.key)}
              className={`text-left p-2.5 rounded-xl border-2 transition-colors ${
                preset === p.key ? 'border-[#C25934] bg-[#C25934]/5' : 'border-[#E8E3D9] hover:bg-[#F0EBE1]'
              }`}
            >
              <p className="font-semibold text-sm text-[#2A2624]">{p.label}</p>
              <p className="text-[10px] text-[#877E78] mt-0.5">{p.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* DEFAULT AI PROMPT — main prompt that will guide every AI image generation */}
      <div data-testid="ai-prompt-section" className="rounded-xl border-2 border-dashed border-[#C25934]/40 bg-[#C25934]/5 p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Sparkles size={16} className="text-[#C25934] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-[#2A2624]">Prompt AI di default</h4>
            <p className="text-[11px] text-[#877E78]">
              Le istruzioni che l'AI userà ogni volta per processare le tue foto prodotto.
              Più sei specifico (stile, sfondo, atmosfera, palette), più i risultati saranno coerenti.
            </p>
          </div>
        </div>

        <textarea
          data-testid="brand-kit-instructions"
          rows={4}
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
          placeholder="Es: foto e-commerce su sfondo bianco, luce morbida da studio, prodotto centrato, palette calda terra-cotta, dettagli del materiale ben visibili..."
          className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-y outline-none focus:ring-2 focus:ring-[#C25934] min-h-[100px]"
        />

        {suggestions.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-[#877E78] uppercase mt-1 mb-1.5">
              💡 Esempi pronti — clicca per inserire (puoi modificare dopo)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`prompt-suggestion-${i}`}
                  onClick={() => setInstructions(s.prompt)}
                  type="button"
                  className="text-[10px] px-2.5 py-1 rounded-full bg-white border border-[#C25934]/30 text-[#C25934] hover:bg-[#C25934]/10 font-medium"
                  title={s.prompt}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Button
        data-testid="save-brand-kit-btn"
        onClick={save}
        disabled={saving}
        className="w-full h-11 rounded-full bg-gradient-to-r from-[#C25934] to-[#8B5CF6] hover:opacity-90 text-white font-semibold"
      >
        {saving ? 'Salvataggio...' : '✨ Salva Brand Kit'}
      </Button>
    </div>
  );
}

function SceneThumb({ path, onDelete }) {
  const url = useStoredImage(path);
  return (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-[#F0EBE1] border border-[#E8E3D9]">
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#877E78]" /></div>}
      <button
        data-testid="delete-scene-btn"
        onClick={onDelete}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500"
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}
